import { formatMinutesToDuration } from "./timeEntryMappers";
import { formatDate } from "../../utils/dateUtils";

export function mapTeamTimeSummaryResponse(raw) {
  // API list shape: { data: [], meta: {} } — rows are grouped per (team, client, project, epic);
  // per-user/work-item breakdown lives in `details[]`.
  const rows = Array.isArray(raw.data) ? raw.data : [];
  const meta = raw.meta;

  return {
    list: rows.map((row) => ({
      id: [row.teamId, row.clientId, row.projectId, row.epicId].filter(Boolean).join("__"),
      teamId: row.teamId,
      teamName: row.teamName,
      clientId: row.clientId,
      clientName: row.clientName,
      projectId: row.projectId,
      projectName: row.projectName,
      epicId: row.epicId,
      epicName: row.epicName,
      epicKey: row.epicKey,
      totalMinutes: row.totalMinutes,
      totalFormatted: row.totalFormatted,
      time: row.totalFormatted || formatMinutesToDuration(row.totalMinutes),
      billableMinutes: row.billableMinutes,
      billableFormatted: row.billableFormatted,
      billableTime: row.billableFormatted || formatMinutesToDuration(row.billableMinutes),
      logCount: row.logCount,
      lastLogDate: row.lastLogDate,
      details: row.details,
    })),
    meta,
  };
}

const formatDateShort = (rawDate) => {
  if (rawDate == null || rawDate === "") return "—";
  const s = formatDate(rawDate);
  return s === "N/A" ? String(rawDate) : s;
};

function deriveTaskLabelFromDetails(details) {
  if (!Array.isArray(details) || details.length === 0) return "—";
  const labels = details.map(
    (d) => [d.workItemKey, d.workItemTitle].filter(Boolean).join(" · ") || "—"
  );
  const unique = [...new Set(labels)];
  if (unique.length === 1) return unique[0];
  return `${unique.length} tasks`;
}

function deriveLoggedDateLabelFromDetails(details) {
  if (!Array.isArray(details) || details.length === 0) return "—";
  const dates = details.map((d) => d.logDate).filter(Boolean);
  if (dates.length === 0) return "—";
  const sorted = [...new Set(dates)].sort();
  const a = formatDateShort(sorted[0]);
  const b = formatDateShort(sorted[sorted.length - 1]);
  return a === b ? a : `${a} – ${b}`;
}

export function mapProjectTimeSummaryResponse(raw) {
  const rows = Array.isArray(raw.data) ? raw.data : [];
  const meta = raw.meta;

  return {
    list: rows.map((row) => {
      const details = row.details || [];
      const task =
        row.task != null && String(row.task).trim() !== ""
          ? String(row.task)
          : row.workItemTitle != null && String(row.workItemTitle).trim() !== ""
            ? String(row.workItemTitle)
            : deriveTaskLabelFromDetails(details);

      let loggedDate;
      if (row.loggedDate != null && String(row.loggedDate).trim() !== "") {
        loggedDate = formatDateShort(row.loggedDate);
      } else if (row.lastLogDate != null && String(row.lastLogDate).trim() !== "") {
        loggedDate = formatDateShort(row.lastLogDate);
      } else {
        loggedDate = deriveLoggedDateLabelFromDetails(details);
      }

      return {
        id: row.projectId,
        projectId: row.projectId,
        project: row.projectName,
        task,
        loggedDate,
        totalMinutes: row.totalMinutes,
        time: row.totalFormatted || formatMinutesToDuration(row.totalMinutes),
        logCount: row.logCount,
        entries: details.map((detail) => ({
          id: detail.id,
          task: [detail.workItemKey, detail.workItemTitle].filter(Boolean).join(" · ") || "—",
          date: formatDateShort(detail.logDate),
          loggedDate: formatDateShort(detail.logDate),
          member: detail.loggedBy?.name || "Unknown",
          memberId: detail.loggedBy?.id ?? null,
          memberEmail: detail.loggedBy?.email ?? "",
          description: detail.comment || "",
          duration: formatMinutesToDuration(detail.minutesLogged),
        })),
      };
    }),
    meta,
  };
}

/**
 * GET /time-logs/developer-summary — one row per (developer, work item, date).
 * `totalMinutesForDate`/`totalFormatted` are already summed server-side across all of that
 * developer's work items for the row's date — every row sharing (developerId, logDate)
 * carries the same total (the "Total Hours" column merges these with rowSpan on the frontend).
 */
export function mapDeveloperTimeSummaryResponse(raw) {
  // API shape: { data: { list: [...], meta: {...} } } — rows already carry both the row's own
  // work item time (loggedMinutes) and the developer's date-level total (totalMinutesForDate).
  const rows = Array.isArray(raw.list) ? raw.list : [];
  const meta = raw.meta;

  return {
    list: rows.map((row) => ({
      id: row.id || [row.developerId, row.workItemId, row.logDate].filter(Boolean).join("__"),
      developerId: row.developerId,
      developerName: row.developerName,
      teamId: row.teamId,
      teamName: row.teamName,
      clientId: row.clientId,
      clientName: row.clientName,
      projectId: row.projectId,
      projectName: row.projectName,
      epicId: row.epicId,
      epicName: row.epicName,
      epicKey: row.epicKey,
      workItemId: row.workItemId,
      workItemTitle: row.workItemTitle,
      workItemKey: row.workItemKey,
      logDate: row.logDate,
      loggedMinutes: row.loggedMinutes,
      loggedFormatted: row.loggedFormatted || formatMinutesToDuration(row.loggedMinutes),
      totalMinutesForDate: row.totalMinutesForDate,
      totalFormatted: row.totalFormatted || formatMinutesToDuration(row.totalMinutesForDate),
      logCount: row.logCount,
      details: row.details,
    })),
    meta,
  };
}

/** Thunk returns axios `response.data` for team/project/developer summary GETs. */
export function mapTeamTimeSummaryFromThunkPayload(apiPayload) {
  const payload = apiPayload?.data ?? apiPayload;
  return mapTeamTimeSummaryResponse({
    data: payload?.data,
    meta: payload?.meta,
  });
}

export function mapProjectTimeSummaryFromThunkPayload(apiPayload) {
  const payload = apiPayload?.data ?? apiPayload;
  return mapProjectTimeSummaryResponse({
    data: payload?.data,
    meta: payload?.meta,
  });
}

export function mapDeveloperTimeSummaryFromThunkPayload(apiPayload) {
  const payload = apiPayload?.data ?? apiPayload;
  return mapDeveloperTimeSummaryResponse({
    list: payload?.list,
    meta: payload?.meta,
  });
}
